import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { round } from 'lodash';
import { combineLatest, interval, Observable, of } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';
import { AircraftHttpClient } from 'src/app/shared/flight-log-api/aircraft/aircraft-client.service';
import { BaseHttpClient } from 'src/app/shared/flight-log-api/bases/base-client.service';
import { DutyLogHttpClient } from 'src/app/shared/flight-log-api/duty-logs/duty-log-client.service';
import { AppState } from 'src/store';
import { AircraftEntity } from 'src/store/aircraft/aircraft.model';
import { selectAircraftById } from 'src/store/aircraft/aircraft.selectors';
import { BaseEntity } from 'src/store/base/base.model';
import { selectBaseById } from 'src/store/base/base.selectors';
import { DutyLogEntity } from 'src/store/duty-log/duty-log.model';
import { formatDuration, getDuration } from 'src/util/moment';
import { filterNull } from 'src/util/rxjs/operators';
import { MINUTE_IN_MS } from 'src/util/time';
import { CurrentUserService } from './current-user.service';
export interface CurrentDutyStatus {
  base?: BaseEntity;
  aircraft?: AircraftEntity;
  dutyLog: DutyLogEntity;
  restDurationDisplay$: Observable<string | null>;
  dutyDurationDisplay$: Observable<string>;
}

@Injectable({
  providedIn: 'root',
})
export class DutyLogService {
  public currentDutyStatus$: Observable<CurrentDutyStatus>;
  constructor(
    private _user: CurrentUserService,
    private _dutyLogClient: DutyLogHttpClient,
    private _aircraft: AircraftHttpClient,
    private _base: BaseHttpClient,
    private _store: Store<AppState>,
  ) {
    this.currentDutyStatus$ = this._user.valueChanges.pipe(
      filterNull(),
      switchMap((u) => u.latestShift$),
      distinctUntilChanged(),
      tap((cs) => {
        if (cs == null) {
          this._dutyLogClient
            .getMany({
              pilotEmployeeId: this._user.value?.id,
              latest: true,
            })
            .toPromise();
        }
      }),
      filterNull(),
      switchMap((latestShift) => {
        const aircraft$: Observable<AircraftEntity | undefined> =
          latestShift.aircraftId != null
            ? this._store.select(selectAircraftById(latestShift.aircraftId))
            : of(undefined);

        const base$: Observable<BaseEntity | undefined> =
          latestShift.baseId != null
            ? this._store.select(selectBaseById(latestShift.baseId))
            : of(undefined);

        return combineLatest([aircraft$, base$]).pipe(
          map(([aircraft, base]) => createDutyStatus(latestShift, aircraft, base)),
        );
      }),
      shareReplay(),
    );

    this.currentDutyStatus$
      .pipe(
        filter((b) => b.base == null),
        map((b) => b.dutyLog.baseId),
        filterNull(),
        distinctUntilChanged(),
      )
      .subscribe((baseIdNeedsFetching) => {
        this._base.getById(baseIdNeedsFetching).toPromise();
      });

    this.currentDutyStatus$
      .pipe(
        filter((b) => b.aircraft == null),
        map((b) => b.dutyLog.aircraftId),
        filterNull(),
        distinctUntilChanged(),
      )
      .subscribe((aircraftIdNeedsFetching) => {
        this._aircraft.getById(aircraftIdNeedsFetching).toPromise();
      });
  }

  public refreshUserDutyLogs() {
    return this._dutyLogClient.getMany({
      pilotEmployeeId: this._user.value?.id,
    });
  }

  public getCurrentDutyStatus() {}
}

export function createDutyStatus(
  dutyLog: DutyLogEntity,
  aircraft: AircraftEntity | undefined,
  base: BaseEntity | undefined,
): CurrentDutyStatus {
  const everyMinute$ = interval(MINUTE_IN_MS);
  const getRestDurationDisplay = () => {
    if (dutyLog.isActive) {
      return null;
    } else {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const rDuration = getDuration(dutyLog.dutyPeriodEndTime!, new Date());
      const restDurationDays = round(rDuration.asDays(), 1);
      const rDurationDisplay =
        rDuration.asDays() >= 1 ? `${restDurationDays}d` : formatDuration(rDuration, 'HH:mm');
      return rDurationDisplay;
    }
  };

  const restDurationDisplay$ = everyMinute$.pipe(
    map(getRestDurationDisplay),
    distinctUntilChanged(),
    startWith(getRestDurationDisplay()),
  );

  const getDutyDurationDisplay = () => {
    const dutyDuration = getDuration(
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dutyLog.dutyPeriodStartTime!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dutyLog.isActive ? new Date() : dutyLog.dutyPeriodEndTime!,
    );
    const dutyDurationDisplay = formatDuration(dutyDuration, 'HH:mm');
    return dutyDurationDisplay;
  };

  const dutyDurationDisplay$ = everyMinute$.pipe(
    map(getDutyDurationDisplay),
    distinctUntilChanged(),
    startWith(getDutyDurationDisplay()),
  );

  return {
    base,
    aircraft,
    dutyLog,
    restDurationDisplay$,
    dutyDurationDisplay$,
  };
}
